import { useCallback, useRef } from 'react';

import type { DecompositionNode } from 'api/data/projects/types';

import { findTreeItem, treeItemHasChildren } from '../utils/treeItem';
import { useChildrenCache } from './useChildrenCache';
import { useTreeItems } from './useTreeItems';

type Input = {
  roots: DecompositionNode[];
  titlePlaceholder?: string;
};

/**
 * Composes the tree's data layers into one reusable bundle: the children cache
 * (`useChildrenCache`), the derived `treeItems`/`itemMetaMap` (`useTreeItems`),
 * stable refs to both, and `openParentIfNeeded`. Shared by the main tree
 * (`DecompositionTreePage`) and the move dialog (`MoveDialogSession`), which each
 * get their own isolated instance.
 *
 * Expansion *control* deliberately stays with each consumer: this hook exposes
 * the primitives (`openParent`, `closeParent`, `closeAllParents`) but does not
 * decide when to call them — the main tree closes parents on collapse, while the
 * dialog only ever opens and tears everything down on unmount.
 */
export function useTreeDataLayer({ roots, titlePlaceholder }: Input) {
  const cache = useChildrenCache();
  const { openParent, loadedChildren, expandedParents, childrenPagination } = cache;

  const { treeItems, itemMetaMap } = useTreeItems({
    roots,
    loadedChildren,
    expandedParents,
    childrenPagination,
    titlePlaceholder
  });

  const treeItemsRef = useRef(treeItems);
  treeItemsRef.current = treeItems;
  const itemMetaMapRef = useRef(itemMetaMap);
  itemMetaMapRef.current = itemMetaMap;

  const openParentIfNeeded = useCallback(
    (treeId: string) => {
      const item = findTreeItem(treeItemsRef.current, treeId);
      if (!treeItemHasChildren(item)) return;
      const meta = itemMetaMapRef.current[treeId];
      if (meta) openParent(treeId, meta.dataNodeId);
    },
    [openParent]
  );

  return { ...cache, treeItems, itemMetaMap, treeItemsRef, itemMetaMapRef, openParentIfNeeded };
}
